import React, { useEffect, useState } from 'react';
import { Alert, AutoComplete, Button, Checkbox, Empty, Form, Image, Input, Modal, Select, Skeleton, Spin, Tag, Typography, message } from 'antd';
import { CarOutlined } from '@ant-design/icons';
import PhoneInput from 'antd-phone-input';
import documentService from '../../services/documentService';
import eventService from '../../services/eventService';
import EventLocationPickerModal from './EventLocationPickerModal';

const { Text } = Typography;

const COLOR_TAGS = { red: 'red', amber: 'orange', green: 'green' };

const formatApiError = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;

  const firstFieldError = Object.values(data?.errors ?? {}).flat().find(Boolean);
  return firstFieldError || data?.message || error?.message || 'Failed to create event';
};

const toPhoneText = (value) =>
  value ? `+${value.countryCode ?? ''}${value.areaCode ?? ''}${value.phoneNumber ?? ''}` : '';

/**
 * Turns a completed New Inspection into an event. The New Inspections
 * counterpart of InspectionRequestModal: the vehicle and company come off the
 * inspection, the user picks the driver and which red/amber findings go in.
 */
const ConvertInspectionToEventModal = ({ documentId, onClose, onConverted }) => {
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [changingCompany, setChangingCompany] = useState(false);
  const [chosenCompany, setChosenCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [choosingVehicle, setChoosingVehicle] = useState(false);
  const [chosenVehicle, setChosenVehicle] = useState(null);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [fleet, setFleet] = useState([]);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [eventLocation, setEventLocation] = useState(null);
  const [showLocationError, setShowLocationError] = useState(false);

  const open = Boolean(documentId);
  const companyId = chosenCompany?.id ?? preview?.companyId ?? null;

  useEffect(() => {
    if (!documentId) {
      form.resetFields();
      setPreview(null);
      setDrivers([]);
      setSelectedFieldIds([]);
      setChangingCompany(false);
      setChosenCompany(null);
      setChoosingVehicle(false);
      setChosenVehicle(null);
      setVehicleSearch('');
      setFleet([]);
      setEventLocation(null);
      setLocationModalOpen(false);
      setShowLocationError(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    documentService
      .getInspectionEventPreview(documentId)
      .then((result) => {
        if (cancelled) return;
        setPreview(result);
        setSelectedFieldIds((result?.findings ?? []).map((finding) => finding.fieldId));
      })
      .catch((error) => {
        console.error('Failed to load inspection for event', error);
        if (!cancelled) {
          setPreview(null);
          message.error(formatApiError(error));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, form]);

  useEffect(() => {
    if (!companyId) {
      setDrivers([]);
      return undefined;
    }

    let cancelled = false;
    setDriversLoading(true);

    eventService
      .getCompanyDrivers(companyId)
      .then((rows) => {
        if (!cancelled) setDrivers((rows || []).filter((driver) => !driver.deleted && !driver.inactive));
      })
      .catch((error) => {
        console.error('Failed to load company drivers:', error);
        message.error('Failed to load drivers');
      })
      .finally(() => {
        if (!cancelled) setDriversLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!changingCompany || companies.length > 0) return undefined;

    let cancelled = false;
    eventService
      .getCompanies()
      .then((rows) => {
        if (!cancelled) setCompanies(rows || []);
      })
      .catch((error) => {
        console.error('Failed to load companies:', error);
        message.error('Failed to load companies');
      });

    return () => {
      cancelled = true;
    };
  }, [changingCompany, companies.length]);

  useEffect(() => {
    const term = vehicleSearch.trim();
    if (!choosingVehicle || !companyId || !term) {
      setFleet([]);
      return undefined;
    }

    let cancelled = false;
    const handle = window.setTimeout(() => {
      setFleetLoading(true);
      documentService
        .getCompanyFleet(companyId, { searchTerm: term })
        .then((page) => {
          if (!cancelled) setFleet(page?.items ?? []);
        })
        .catch((error) => {
          console.error('Failed to search company fleet:', error);
          if (!cancelled) setFleet([]);
        })
        .finally(() => {
          if (!cancelled) setFleetLoading(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [choosingVehicle, companyId, vehicleSearch]);

  const alreadyConverted = Boolean(preview?.eventId);
  const inspectionHasVehicle = Boolean(preview?.vehicle?.vehicleId);
  const missingVehicle = Boolean(preview) && !inspectionHasVehicle && !chosenVehicle;
  const locked = alreadyConverted || missingVehicle;

  const handleCompanyChange = (nextCompanyId) => {
    const company = companies.find((item) => String(item.id) === String(nextCompanyId));
    setChosenCompany(company ? { id: company.id, name: company.name } : null);
    setChangingCompany(false);
    form.setFieldValue('driverId', undefined);
    setChosenVehicle(null);
    setVehicleSearch('');
  };

  const handleVehicleSelect = (vehicleId) => {
    setChosenVehicle(fleet.find((vehicle) => vehicle.id === vehicleId) ?? null);
    setChoosingVehicle(false);
    setVehicleSearch('');
  };

  const toggleFinding = (fieldId) =>
    setSelectedFieldIds((previous) =>
      previous.includes(fieldId) ? previous.filter((id) => id !== fieldId) : [...previous, fieldId]
    );

  const handleSubmit = async () => {
    if (!eventLocation) {
      setShowLocationError(true);
      message.error('An event location is required to create the event.');
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch {
      document.querySelector('.ant-form-item-has-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      const result = await documentService.convertInspectionToEvent(documentId, {
        driverId: values.driverId,
        controllerName: values.controllerName.trim(),
        controllerNumber: toPhoneText(values.controllerNumber),
        note: values.note.trim(),
        selectedFieldIds,
        companyId: chosenCompany?.id,
        vehicleId: chosenVehicle?.id,
        latitude: eventLocation?.latitude,
        longitude: eventLocation?.longitude,
        address: eventLocation?.address,
      });

      message.success(
        result?.eventReference ? `Event ${result.eventReference} created` : 'Event created successfully'
      );
      onConverted?.(result);
      onClose();
    } catch (error) {
      console.error('Failed to convert inspection to event', error);
      message.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const vehicle = chosenVehicle
    ? { ...chosenVehicle, odometer: preview?.vehicle?.odometer }
    : preview?.vehicle;
  const vehicleRows = [
    ['Registration', vehicle?.registration],
    ['Fleet', vehicle?.fleet],
    ['Make', vehicle?.make],
    ['Model', vehicle?.model],
    ['Year', vehicle?.year],
    ['VIN', vehicle?.vin],
    ['Odometer', vehicle?.odometer],
  ];

  const renderBody = () => {
    if (loading) return <Skeleton active />;
    if (!preview) return <Alert type="error" showIcon message="Could not load this inspection." />;

    return (
      <>
        {alreadyConverted && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`This inspection was already converted to event ${preview.eventReference ?? ''}.`}
          />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text strong>Company</Text>
          {!alreadyConverted && !changingCompany && (
            <Button size="small" onClick={() => setChangingCompany(true)}>
              Change Company
            </Button>
          )}
        </div>
        <div style={{ marginBottom: 12, marginTop: 4 }}>
          {changingCompany ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                showSearch
                optionFilterProp="label"
                style={{ flex: 1, minWidth: 0 }}
                options={companies.map((company) => ({ value: company.id, label: company.name }))}
                value={companyId ?? undefined}
                onChange={handleCompanyChange}
                placeholder="Select a company"
                notFoundContent={companies.length === 0 ? <Spin size="small" /> : 'No companies found'}
              />
              <Button onClick={() => setChangingCompany(false)}>Cancel</Button>
            </div>
          ) : (
            <Text type="secondary">{chosenCompany?.name || preview.companyName || 'N/A'}</Text>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Text strong>
            <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Location
          </Text>
          {!alreadyConverted && (
            <Button size="small" onClick={() => setLocationModalOpen(true)}>
              {eventLocation ? 'Change Location' : 'Add Location'}
            </Button>
          )}
        </div>
        <div style={{ marginBottom: 12, marginTop: 4 }}>
          {eventLocation ? (
            <>
              <div>
                <Text type="secondary">{eventLocation.address || 'No address'}</Text>
              </div>
              <div>
                <Text type="secondary">
                  {Number(eventLocation.latitude).toFixed(6)}, {Number(eventLocation.longitude).toFixed(6)}
                </Text>
              </div>
            </>
          ) : (
            <Text type="secondary">No location set</Text>
          )}
          {showLocationError && !alreadyConverted && !eventLocation && (
            <div>
              <Text type="danger" style={{ fontSize: 12 }}>
                An event location is required to create the event.
              </Text>
            </div>
          )}
        </div>

        {!inspectionHasVehicle && (
          <div style={{ marginBottom: 12 }}>
            {missingVehicle && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message="No vehicle was selected from the fleet on this inspection. Select one to convert it to an event."
              />
            )}

            {!alreadyConverted && (choosingVehicle ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <AutoComplete
                  style={{ flex: 1, minWidth: 0 }}
                  options={fleet.map((item) => ({
                    value: item.id,
                    label: [item.registration, item.fleet, [item.make, item.model].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(' · '),
                  }))}
                  value={vehicleSearch}
                  onChange={setVehicleSearch}
                  onSelect={handleVehicleSelect}
                  filterOption={false}
                  placeholder="Search registration, fleet or VIN"
                  notFoundContent={
                    fleetLoading
                      ? <Spin size="small" />
                      : vehicleSearch.trim() ? 'No vehicles found for this company' : null
                  }
                />
                <Button onClick={() => setChoosingVehicle(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="small" icon={<CarOutlined />} onClick={() => setChoosingVehicle(true)}>
                {chosenVehicle ? 'Change Vehicle' : 'Select Vehicle'}
              </Button>
            ))}
          </div>
        )}

        <Text strong>Vehicle Details</Text>
        <div style={{ marginTop: 6, marginBottom: 16 }}>
          {vehicleRows.map(([label, value]) => (
            <div key={label}>
              <Text type="secondary">
                {label}: {value || 'N/A'}
              </Text>
            </div>
          ))}
        </div>

        <Form form={form} layout="vertical" disabled={locked}>
          <Form.Item label="Driver" name="driverId" rules={[{ required: true, message: 'Please select a driver' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={drivers.map((driver) => ({ value: driver.id, label: driver.fullName }))}
              loading={driversLoading}
              placeholder="Select driver"
              notFoundContent={driversLoading ? <Spin size="small" /> : 'No drivers found'}
            />
          </Form.Item>

          <Form.Item
            name="controllerName"
            label="Controller Name"
            rules={[{ required: true, whitespace: true, message: 'Please enter controller name' }]}
          >
            <Input placeholder="Controller Name" />
          </Form.Item>

          <Form.Item
            name="controllerNumber"
            label="Controller Number"
            required
            rules={[
              {
                validator: (_, value) =>
                  value && value.valid?.()
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter a valid controller number')),
              },
            ]}
          >
            <PhoneInput enableSearch />
          </Form.Item>

          <Form.Item
            name="note"
            label="Other Notes"
            rules={[{ required: true, whitespace: true, message: 'Please enter other notes' }]}
          >
            <Input.TextArea placeholder="Other Notes" autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Form>

        <Text strong>Select event description</Text>

        {preview.findings?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {preview.findings.map((finding) => {
              const checked = selectedFieldIds.includes(finding.fieldId);

              return (
                <div
                  key={finding.fieldId}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 12,
                    opacity: checked ? 1 : 0.6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onChange={() => toggleFinding(finding.fieldId)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text strong>{finding.fieldName}</Text>{' '}
                      <Tag color={COLOR_TAGS[finding.color] ?? 'default'}>
                        {finding.color ? finding.color.charAt(0).toUpperCase() + finding.color.slice(1) : ''}
                      </Tag>
                      {finding.comment && (
                        <div>
                          <Text type="secondary">{finding.comment}</Text>
                        </div>
                      )}
                    </div>
                  </div>

                  {finding.images?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      <Image.PreviewGroup>
                        {finding.images.map((image) => (
                          <Image
                            key={image.imageId}
                            src={image.url}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                          />
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <Empty description="This inspection has no red or amber findings." />
        )}
      </>
    );
  };

  return (
    <>
      <Modal
        className="inspection-modal"
        title={preview?.reference ? `Event — ${preview.reference}` : 'Event'}
        open={open}
        onCancel={onClose}
        destroyOnHidden
        width={window.innerWidth < 768 ? '95%' : 560}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
        footer={
          <Button
            icon={<CarOutlined />}
            type="primary"
            loading={submitting}
            disabled={!preview || locked || submitting}
            onClick={handleSubmit}
          >
            Create Event
          </Button>
        }
      >
        {renderBody()}
      </Modal>

      <EventLocationPickerModal
        open={locationModalOpen}
        initial={eventLocation}
        onClose={() => setLocationModalOpen(false)}
        onSave={(location) => {
          setEventLocation(location);
          setShowLocationError(false);
          setLocationModalOpen(false);
        }}
      />
    </>
  );
};

export default ConvertInspectionToEventModal;
